import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Check, X } from 'lucide-react';
import apiService from './apiService';
import { useAlert } from './contexts/AlertContext';

const ChangePassword = () => {
    const { showAlert } = useAlert();
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [loading, setLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);

    const checkPasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength++;
        if (password.length >= 12) strength++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[^a-zA-Z\d]/.test(password)) strength++;
        return strength;
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setFormData({ ...formData, newPassword });
        setPasswordStrength(checkPasswordStrength(newPassword));
    };

    const getStrengthColor = () => {
        if (passwordStrength <= 2) return 'bg-red-500';
        if (passwordStrength === 3) return 'bg-yellow-500';
        if (passwordStrength === 4) return 'bg-blue-500';
        return 'bg-green-500';
    };

    const getStrengthText = () => {
        if (passwordStrength <= 2) return 'Weak';
        if (passwordStrength === 3) return 'Fair';
        if (passwordStrength === 4) return 'Good';
        return 'Strong';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (formData.newPassword.length < 8) {
            showAlert('New password must be at least 8 characters long', 'error');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            showAlert('New passwords do not match', 'error');
            return;
        }

        if (formData.currentPassword === formData.newPassword) {
            showAlert('New password must be different from current password', 'error');
            return;
        }

        setLoading(true);

        try {
            const response = await apiService.changePassword(
                formData.currentPassword,
                formData.newPassword
            );

            if (response.changePassword.success) {
                showAlert(response.changePassword.message, 'success');
                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setPasswordStrength(0);
            } else {
                showAlert(response.changePassword.message, 'error');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            showAlert('Failed to change password. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const passwordRequirements = [
        { met: formData.newPassword.length >= 8, text: 'At least 8 characters' },
        { met: /[a-z]/.test(formData.newPassword) && /[A-Z]/.test(formData.newPassword), text: 'Upper and lowercase letters' },
        { met: /\d/.test(formData.newPassword), text: 'At least one number' },
        { met: /[^a-zA-Z\d]/.test(formData.newPassword), text: 'At least one special character' },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold">Change Password</h2>
                <p className="text-gray-600 dark:text-gray-400">Update your password to keep your account secure</p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 max-w-2xl">
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Current Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Current Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.current ? 'text' : 'password'}
                                value={formData.currentPassword}
                                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white pr-10"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                                {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>

                    {/* New Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.new ? 'text' : 'password'}
                                value={formData.newPassword}
                                onChange={handlePasswordChange}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white pr-10"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                                {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>

                        {/* Password Strength Indicator */}
                        {formData.newPassword && (
                            <div className="mt-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full ${getStrengthColor()} transition-all duration-300`}
                                            style={{ width: `${(passwordStrength / 5) * 100}%` }}
                                        />
                                    </div>
                                    <span className={`text-sm font-medium ${
                                        passwordStrength <= 2 ? 'text-red-500' :
                                        passwordStrength === 3 ? 'text-yellow-500' :
                                        passwordStrength === 4 ? 'text-blue-500' :
                                        'text-green-500'
                                    }`}>
                                        {getStrengthText()}
                                    </span>
                                </div>

                                {/* Password Requirements */}
                                <div className="mt-3 space-y-1">
                                    {passwordRequirements.map((req, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                            {req.met ? (
                                                <Check className="w-4 h-4 text-green-500" />
                                            ) : (
                                                <X className="w-4 h-4 text-gray-400" />
                                            )}
                                            <span className={req.met ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}>
                                                {req.text}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPasswords.confirm ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white pr-10"
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                                {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                            <p className="mt-1 text-sm text-red-500">Passwords do not match</p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <div className="flex items-center gap-3">
                        <button
                            type="submit"
                            disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword || formData.newPassword !== formData.confirmPassword}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <Lock className="w-4 h-4" />
                            {loading ? 'Changing Password...' : 'Change Password'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Security Tips */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-2xl">
                <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-2">Security Tips</h3>
                <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-400">
                    <li>• Use a unique password you don't use on other websites</li>
                    <li>• Combine letters, numbers, and special characters</li>
                    <li>• Avoid using personal information like names or birthdays</li>
                    <li>• Change your password regularly for better security</li>
                </ul>
            </div>
        </div>
    );
};

export default ChangePassword;

